/**
 * ## DAL - Domain Access Layer
 *
 * The primary interface for client developers to update the application
 * data model locally and remotely.
 *
 * Synchronization with connected peers is automatic provided this client
 * is connected to the remote Hub or Cloud sync server.
 *
 * @module client/dal
 */

"use strict";

const state = require("../state");
const Localstore = require("./localstore");
const config = require("../../config/client.js");
const embed = require("../embed");

let session_id = parseInt(Math.random() * 90000, 10) + 10000;

/**
 * Credential
 * Adds credential information to the _kd metadata object
 * @param {Object} proposed - the data
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @returns {Object} object with _kd metadata added
 */
const credential = (proposed, _localstore) => {
  const proposedData = proposed;
  if (!proposedData.hasOwnProperty("_kd")) {
    proposedData._kd = {};
  }

  const creds = _localstore.data.credentials;
  proposedData.service = _localstore.data.config.service;
  proposedData.api = _localstore.data.config.api;
  proposedData._kd.transport = "socket";
  proposedData._kd.site_id = creds.site_id;
  proposedData._kd.user_id = creds.user_id;
  proposedData._kd.room = _localstore.data.config.room;
  proposedData._kd.updated = new Date().getTime();
  proposedData._kd.session_id = session_id;
  if (_localstore.data.offline === true) {
    proposedData._kd.offline = true;
  }
  return proposedData;
};

const record = (pro, _localstore, kd) => {
  let proposed = Object.assign({}, pro);
  let action = "New";
  if (proposed.hasOwnProperty("_id")) {
    action = "Update";
  }
  if (
    !proposed.hasOwnProperty("_kd") ||
    !proposed._kd.hasOwnProperty("confirmed")
  ) {
    if (proposed.hasOwnProperty("_id")) {
      proposed = kd.doc(proposed.domain, proposed._id)[0];
    }
    if (typeof proposed !== "undefined" && proposed.hasOwnProperty("domain")) {
      switch (proposed.domain) {
        case "activity": {
          switch (proposed.status) {
            case 1: {
              action += " : Waitlist";
              break;
            }
            case 2: {
              switch (proposed.type) {
                case 1: {
                  action += " : SMS Alerted";
                  break;
                }
                case 2: {
                  action += " : Automated Call Alerted";
                  break;
                }
                case 3: {
                  action += " : Pager Alerted";
                  break;
                }
                case 4: {
                  action += " : Email Alerted";
                  break;
                }
                case 5: {
                  action += " : Guest Waiting Alerted";
                  break;
                }
                default: {
                  break;
                }
              }
              break;
            }
            case 3: {
              action += " : Seated";
              break;
            }
            case 5: {
              action += " : Completed";
              break;
            }
            case 6: {
              action += " : Moved to no shows";
              break;
            }
            case 7: {
              action += " : Moved to pre-check";
              break;
            }
            default: {
              break;
            }
          }
          break;
        }
        case "reservation": {
          switch (proposed.status) {
            case 1: {
              action += " : Reservation";
              break;
            }
            case 4: {
              action += " : Seated";
              break;
            }
            default: {
              break;
            }
          }
          break;
        }
        default: {
          break;
        }
      }
    }
    if (typeof ga !== "undefined") {
      ga(
        "send",
        "event",
        proposed.domain,
        action,
        `/${kd.localstore.data.credentials.site_id}`
      );
    }
  }
  return true;
};

/**
 * Update or insert data
 * Please use kd.update for client requests
 *
 * @param {Object} proposed - formatted json of the change data.
 * Only include data structure that has changed, all existing data will be
 * maintained and updated against proposed data. Will be checked against
 * validation JSON schema before processing and add credentails.
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @param {Object} _socket - Socket connected to the site's namespace
 * @param {Object} db - db assigned to current kdclient instance
 * @param {Object} kd - authenticated kd instance to emit local events
 *
 * @returns {Promise} resolving to change object
 */
const update = (proposed, _localstore, _socket, db, kd) => {
  if (
    typeof kd !== "undefined" &&
    kd.hasOwnProperty("mode") &&
    kd.mode !== "xenial" &&
    kd.hasOwnProperty("doc")
  ) {
    record(proposed, _localstore, kd);
  }
  const credentialedData = credential(proposed, _localstore);
  return state.update(credentialedData, db).then((result) => {
    if (
      proposed.hasOwnProperty("_kd") &&
      !proposed._kd.hasOwnProperty("confirmed")
    ) {
      if (
        kd &&
        proposed._kd.hasOwnProperty("session_id") &&
        proposed._kd.session_id !== session_id
      ) {
        kd.emit(`${result.domain}.update`, result);
      }
      // get offline status
      if (
        _localstore.data.credentials.site_id !== "embed" &&
        kd.mode !== "xenial"
      ) {
        if (!_localstore.data.offline) {
          _socket.emit(`${result.domain}.update`, result);
        } else {
          kd.emit(`${result.domain}.update`, result);
        }
      } else {
        embed.emit(result.domain, result, kd);
      }
    }
    return result;
  });
};

/**
 * Update or insert data for microservice
 * * Please use kd.update_ms for client requests
 *
 * When updates objects from a mircoservice back to the server and then clients,
 * you must pass back the entire object received with the request received on
 * the kd.stream.event (kitchen_display, print, etc) inside the coreservices
 * room. Taking that same object, updating the properties inside the
 * coreservices[0] object will send to back to the server, connected clients,
 * and microservices. The coreservices[0] object must contain .id and .service
 * properties, which match the event being listened to in the client.
 *
 * @param {Object} proposed - change json object
 * @param {Object} _localstore
 * @param {Object} _socket
 *
 */
const update_ms = (proposed, _localstore, _socket) => {
  const credentialedData = credential(proposed, _localstore);
  // add validation here
  if (credentialedData.hasOwnProperty("coreservices")) {
    _socket.emit(
      `${credentialedData.coreservices[0].service}.update`,
      credentialedData
    );
    return true;
  }
  return false;
};

/**
 * Initialize
 * Please use kd.initialize for client requests
 *
 * Gets sync data from the server based on time.
 * syncRequest.lastsync is unixtime*1000, (javascript new Data.getTime())
 * from when you want to retrieve all documents from a domain since that time.
 * if you set .sync to 0 it will retrieve all documents from that
 * domain
 *
 * @param {Object} syncRequest
 * @param {string} syncRequest.domain
 * @param {string} syncRequest.lastsync - milliseconds since 1970 or "0"
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @param {Object} _socket - Socket connected to the site's namespace
 * @param {Object} db - db assigned to current kdclient instance
 */
const initialize = (syncRequest, _localstore, _socket, db) => {
  if (syncRequest.domain && syncRequest.lastsync > -1) {
    if (syncRequest.lastsync === 0) {
      config.domains.forEach((domain) => {
        try {
          db.collections[domain].removeWhere({});
        } catch (e) {
          console.log(e);
        }
      });
    }
    const credentialedData = credential(syncRequest, _localstore);
    return state.initialize(credentialedData, _socket, db);
  }
  return false;
};

/**
 * Initialize
 * Please use kd.initialize for client requests
 *
 * @param {Object} sync_request
 * @param {string} sync_request.domain
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @param {Object} _socket - Socket connected to the site's namespace
 */
const initialize_ms = (syncRequest, _localstore, _socket) => {
  const service = _localstore.data.config.service;
  if (syncRequest.domain && service) {
    const credentialedData = credential(syncRequest, _localstore);
    _socket.emit(`${service}.initialize`, credentialedData);
    return true;
  }
  return false;
};

/**
 * Publish for dataservices over socket
 *
 * @param {Object} data - json object to publish
 * @param {string} data.service - service to publish to i.e. pipeline
 * @param {string} data.system - System format i.e. Dinerware
 * @param {string} data.version - System version
 * @param {string} data.payload - Data to be fed to RabbitMQ.
 *   If JSON, include as property string
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @param {Object} _socket - Socket connected to the site's namespace
 */
const publish_ds = (data, _localstore, _socket) => {
  const service = _localstore.data.config.service;
  _socket.emit(`${service}.publish`, data);
  return true;
};

/**
 * Update or insert data
 * Please use kd.update for client requests
 *
 * @param {Object} proposed - formatted json of the change data.
 * Only include data structure that has changed, all existing data will be
 * maintained and updated against proposed data. Will be checked against
 * validation JSON schema before processing and add credentails.
 * @param {Object} _localstore - _localstore assigned to current kdclient instance
 * @param {Object} _socket - Socket connected to the site's namespace
 * @param {Object} db - db assigned to current kdclient instance
 *
 * @returns {Promise} resolving to change object
 */
const aux = (request, _localstore, _socket) => {
  // get offline status
  if (!_localstore.data.offline) {
    _socket.emit("aux", request);
    return true;
  }
  return false;
};

const rpc = (request, _localstore, _socket) => {
  // , db
  const credentialedData = credential(request, _localstore);
  if (!_localstore.data.offline) {
    _socket.emit("rpc", credentialedData);
    return true;
  }
  return false;
};

module.exports = {
  credential,
  Localstore,
  update,
  update_ms,
  initialize,
  initialize_ms,
  publish_ds,
  aux,
  rpc,
};



/** WEBPACK FOOTER **
 ** ./src/client/dal/index.js
 **/